"use client";

import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import Link from "next/link";
import { useAuth } from "@/lib/context/AuthProvider";
import { useLocale } from "@/lib/context/LocaleProvider";
import {
  type ItemLocation,
  useProjects,
} from "@/lib/context/ProjectsProvider";
import { useUI } from "@/lib/context/UIProvider";
import { useGenerationHistory } from "@/lib/hooks/useGenerationHistory";
import { useReportPageLoading } from "@/lib/hooks/useReportPageLoading";
import {
  formatGenerationDate,
  isGenerationToday,
  type ApiGenerationHistoryItem,
} from "@/lib/api/generations";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/Badge";
import { HistoryListItem } from "@/components/ui/HistoryListItem";
import { ProjectCard } from "@/components/ui/ProjectCard";
import { ProjectsProCta } from "@/components/ui/ProjectsProCta";
import {
  HistoryListSkeleton,
  ProjectGridSkeleton,
} from "@/components/ui/Skeleton";
import { Pagination } from "@/components/ui/Pagination";
import { ArrowRight, Folder, History, Plus } from "@/components/ui/icons";

/** Create-project CTA — Pro-only; guests / free users get the paywall + PRO badge. */
function CreateProjectButton({
  className,
  plusSize,
  /** Solid accent fill — darken the PRO pill so it stays readable. */
  solid = false,
}: {
  className: string;
  plusSize: number;
  solid?: boolean;
}) {
  const { t } = useLocale();
  const { isPro } = useAuth();
  const { openProModal } = useUI();

  if (!isPro) {
    return (
      <button type="button" onClick={openProModal} className={className}>
        <Plus size={plusSize} />
        <span>{t.createProject}</span>
        <Badge
          variant="pro"
          size="sm"
          label={t.badgePro}
          className={
            solid ? "border-white/75 bg-[rgba(26,24,22,0.28)]" : undefined
          }
        />
      </button>
    );
  }

  return (
    <Link href="/projects/new" className={className}>
      <Plus size={plusSize} />
      <span>{t.createProject}</span>
    </Link>
  );
}

/** Map a local guest draft onto the History list row shape. */
function guestLocationToHistoryItem(
  loc: ItemLocation,
): ApiGenerationHistoryItem {
  const createdAt = loc.item.createdAt
    ? new Date(loc.item.createdAt).toISOString()
    : new Date().toISOString();
  return {
    id: 0,
    slug: loc.item.slug,
    title: loc.item.name,
    toolTitle: loc.item.tool,
    modality: loc.item.modality ?? "",
    project: {
      slug: loc.project.slug,
      name: loc.project.name,
      description: loc.project.desc,
    },
    createdAt,
  };
}

/** Calm empty panel for Projects / History tabs. */
function EmptyPanel({
  icon,
  title,
  description,
  action,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  action: ReactNode;
}) {
  return (
    <div
      className="relative overflow-hidden rounded-[20px] border border-dashed border-[rgba(26,24,22,0.14)] bg-surface px-6 py-14 text-center"
      style={{
        backgroundImage:
          "linear-gradient(165deg, color-mix(in srgb, var(--accent) 9%, transparent) 0%, transparent 52%)",
      }}
    >
      <div className="relative mx-auto flex max-w-[360px] flex-col items-center">
        <div className="mb-4 flex h-[56px] w-[56px] items-center justify-center rounded-[16px] bg-accent-soft text-accent shadow-[0_1px_2px_rgba(45,32,50,0.06)]">
          {icon}
        </div>
        <h2 className="text-[17px] font-semibold tracking-[-0.3px] text-ink">
          {title}
        </h2>
        <p className="mt-2 text-[14px] leading-[1.5] text-ink-3 [text-wrap:pretty]">
          {description}
        </p>
        <div className="mt-6">{action}</div>
      </div>
    </div>
  );
}

/** /projects — project grid with a Projects / History toggle. */
export function ProjectsScreen() {
  const { t, locale } = useLocale();
  const { isAuthenticated, isPro } = useAuth();
  const {
    projects,
    allItems,
    hydrated,
    page,
    totalPages,
    listLoading,
    setProjectsPage,
    refreshProjects,
    guestSyncError,
    retryGuestSync,
  } = useProjects();
  const [view, setView] = useState<"projects" | "history">("projects");
  const {
    items: historyItems,
    page: historyPage,
    totalPages: historyTotalPages,
    loading: historyLoading,
    error: historyError,
    loaded: historyLoaded,
    setHistoryPage,
    retry: retryHistory,
  } = useGenerationHistory(view === "history" && isAuthenticated);

  useReportPageLoading(
    view === "projects"
      ? !hydrated
      : isAuthenticated
        ? !historyLoaded
        : !hydrated,
  );

  const guestHistoryItems = useMemo(
    () => allItems.map(guestLocationToHistoryItem),
    [allItems],
  );

  const segBase =
    "rounded-full px-3.5 py-[7px] text-[12.5px] font-semibold transition";

  // Refresh only when returning from History — initial list comes from ProjectsProvider.
  const prevViewRef = useRef(view);
  useEffect(() => {
    const prev = prevViewRef.current;
    prevViewRef.current = view;
    if (view === "projects" && prev === "history") {
      refreshProjects();
    }
  }, [view, refreshProjects]);

  const onProjectsPageChange = (nextPage: number) => {
    setProjectsPage(nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onHistoryPageChange = (nextPage: number) => {
    setHistoryPage(nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const emptyHistoryAction =
    projects.length === 0 ? (
      <CreateProjectButton
        solid
        plusSize={16}
        className="inline-flex h-[46px] items-center justify-center gap-[7px] rounded-full bg-accent px-6 text-[14px] font-semibold text-white transition hover:brightness-105"
      />
    ) : (
      <Link
        href="/tools"
        className="inline-flex h-[46px] items-center justify-center gap-[7px] rounded-full bg-accent px-6 text-[14px] font-semibold text-white transition hover:brightness-105"
      >
        <span>{t.emptyHistoryCta}</span>
        <ArrowRight size={15} />
      </Link>
    );

  let historyBody: ReactNode;
  if (!isAuthenticated) {
    if (!hydrated) {
      historyBody = <HistoryListSkeleton />;
    } else if (guestHistoryItems.length === 0) {
      historyBody = (
        <EmptyPanel
          icon={<History size={24} />}
          title={t.emptyHistoryTitle}
          description={t.emptyHistoryDesc}
          action={emptyHistoryAction}
        />
      );
    } else {
      historyBody = (
        <div className="overflow-hidden rounded-[20px] border border-[rgba(26,24,22,0.05)] bg-surface">
          {guestHistoryItems.map((item) => (
            <HistoryListItem
              key={item.slug}
              item={item}
              dateLabel={
                isGenerationToday(item.createdAt)
                  ? t.tfToday
                  : formatGenerationDate(item.createdAt, locale)
              }
            />
          ))}
        </div>
      );
    }
  } else if (!historyLoaded) {
    historyBody = <HistoryListSkeleton />;
  } else if (historyError) {
    historyBody = (
      <EmptyPanel
        icon={<History size={24} />}
        title={t.emptyHistoryTitle}
        description={historyError}
        action={
          <button
            type="button"
            onClick={retryHistory}
            className="inline-flex h-[46px] items-center justify-center gap-[7px] rounded-full bg-accent px-6 text-[14px] font-semibold text-white transition hover:brightness-105"
          >
            {t.retry}
          </button>
        }
      />
    );
  } else if (historyItems.length === 0) {
    historyBody = (
      <EmptyPanel
        icon={<History size={24} />}
        title={t.emptyHistoryTitle}
        description={t.emptyHistoryDesc}
        action={emptyHistoryAction}
      />
    );
  } else {
    historyBody = (
      <>
        <div
          className={cn(
            "overflow-hidden rounded-[20px] border border-[rgba(26,24,22,0.05)] bg-surface transition-opacity",
            historyLoading && "pointer-events-none opacity-50",
          )}
        >
          {historyItems.map((item) => (
            <HistoryListItem
              key={item.id}
              item={item}
              dateLabel={
                isGenerationToday(item.createdAt)
                  ? t.tfToday
                  : formatGenerationDate(item.createdAt, locale)
              }
            />
          ))}
        </div>
        <Pagination
          page={historyPage}
          totalPages={historyTotalPages}
          onPageChange={onHistoryPageChange}
          disabled={historyLoading}
        />
      </>
    );
  }

  return (
    <section className="animate-fade-up px-5 pt-7">
      <h1 className="mb-2 text-[32px] font-medium leading-[1.05] tracking-[-1.28px]">
        {t.projectsTitle}
      </h1>
      <p className="mb-5 text-[15px] leading-[1.45] text-ink-3">
        {t.projectsDesc}
      </p>

      {guestSyncError ? (
        <div className="mb-5 flex flex-col gap-3 rounded-[16px] border border-[rgba(26,24,22,0.1)] bg-surface px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[13.5px] leading-[1.45] text-ink-2 [text-wrap:pretty]">
            {guestSyncError}
          </p>
          <button
            type="button"
            onClick={retryGuestSync}
            className="inline-flex h-[38px] shrink-0 items-center justify-center rounded-full bg-accent px-4 text-[13px] font-semibold text-white transition hover:brightness-105"
          >
            {t.retry}
          </button>
        </div>
      ) : null}

      <div className="mb-5 flex items-center justify-between gap-3">
        <div className="inline-flex gap-0.5 rounded-full bg-[rgba(26,24,22,0.05)] p-[3px]">
          {(["projects", "history"] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={cn(
                segBase,
                view === v
                  ? "bg-white text-ink shadow-[rgba(26,24,22,0.08)_0_1px_2px]"
                  : "bg-transparent text-muted",
              )}
            >
              {v === "projects" ? t.projTab : t.histTab}
            </button>
          ))}
        </div>
        <CreateProjectButton
          plusSize={17}
          className="inline-flex flex-none items-center gap-[7px] px-1 py-2 text-[14px] font-semibold tracking-[-0.2px] text-accent transition-opacity hover:opacity-60"
        />
      </div>

      {view === "projects" ? (
        !hydrated ? (
          <ProjectGridSkeleton />
        ) : projects.length === 0 && !listLoading ? (
          <EmptyPanel
            icon={<Folder size={24} />}
            title={t.emptyProjectsTitle}
            description={t.emptyProjectsDesc}
            action={
              <CreateProjectButton
                solid
                plusSize={16}
                className="inline-flex h-[46px] items-center justify-center gap-[7px] rounded-full bg-accent px-6 text-[14px] font-semibold text-white transition hover:brightness-105"
              />
            }
          />
        ) : (
          <>
            <div
              className={cn(
                "grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(320px,1fr))] transition-opacity",
                listLoading && "pointer-events-none opacity-50",
              )}
            >
              {projects.map((project) => (
                <Fragment key={project.id}>
                  <ProjectCard project={project} />
                  {!isPro && project.slug === "default" ? (
                    <ProjectsProCta />
                  ) : null}
                </Fragment>
              ))}
            </div>
            {isAuthenticated ? (
              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={onProjectsPageChange}
                disabled={listLoading}
              />
            ) : null}
          </>
        )
      ) : (
        historyBody
      )}
    </section>
  );
}
